import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import PredefinedDateRanges from "../../../core/common/datePicker";
import Table from "../../../core/common/dataTable";
import { all_routes } from "../../router/all_routes";
import {
  getToday,
  openSchool,
  closeSchool,
  getStaffOptions,
  list,
  OperatingDayFull,
  TodayResponse,
  OperatingStatus,
  OpenedForRef,
} from "../../../service/operatingDay";
import { getAllClasses } from "../../../service/classes";
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice";
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";

// ── helpers ──────────────────────────────────────────────────────────────
const STAFF_EXCLUDE = ["parent", "student", "owner"];
// Grouped by Class.stage (the board-defined stage, e.g. Primary / Stage 3 /
// Higher Secondary — see models/Class.js). A campus only ever exposes the
// stages of the systems it actually runs, so no fixed label map is needed
// here — the group headers are the stage values themselves.
const UNGROUPED_STAGE = "Ungrouped";
// Display order for stage buckets — Class.stage's enum values (models/Class.js)
// in age progression, plus the fallback bucket last.
const STAGE_DISPLAY_ORDER = [
  "Pre-primary",
  "Stage 1",
  "Primary",
  "Stage 2",
  "Stage 3",
  "Lower Secondary",
  "Stage 4",
  "Secondary",
  "Stage 5",
  "Higher Secondary",
  UNGROUPED_STAGE,
];

const fullName = (u: OpenedForRef | string | undefined): string => {
  if (!u) return "—";
  if (typeof u === "string") return u;
  return [u.firstName, u.lastName].filter(Boolean).join(" ") || u.name || "—";
};
const roleName = (r: any): string =>
  (typeof r?.role === "object" ? r.role?.name : r?.role) || "Unknown";

const fmtDate = (d?: string) => (d ? dayjs(d).format("DD MMM YYYY") : "—");
const fmtDay = (d?: string) => (d ? dayjs(d).format("dddd") : "—");
const fmtTime = (d?: string) => (d ? dayjs(d).format("hh:mm A") : "—");

const StatusBadge: React.FC<{ status?: OperatingStatus | string }> = ({ status }) => {
  const map: Record<string, { cls: string; icon: string; label: string }> = {
    OPENED: { cls: "badge-soft-success", icon: "ti-lock-open", label: "Opened" },
    CLOSED: { cls: "badge-soft-danger", icon: "ti-lock", label: "Closed" },
    PENDING: { cls: "badge-soft-warning", icon: "ti-clock", label: "Pending" },
  };
  const s = map[status || "PENDING"] || map.PENDING;
  return (
    <span className={`badge ${s.cls} d-inline-flex align-items-center`}>
      <i className={`ti ${s.icon} me-1`} />
      {s.label}
    </span>
  );
};

type ClassNode = { _id: string; name: string; sections: OpenedForRef[] };

// The bucket a class is displayed under: its board `stage` if it has one
// (Matric/O-Level campuses), else its academic class group (Group model —
// e.g. "BSN-Generic" for a PNMC campus, sent as `classGroup` by the API),
// else "Ungrouped".
const classBucket = (c: any): string =>
  c?.stage || c?.classGroup?.name || UNGROUPED_STAGE;

// Order bucket keys: known board stages first (age progression), then any
// class-group buckets alphabetically, then "Ungrouped" last.
const orderedBuckets = (keys: string[]): string[] => {
  const known = STAGE_DISPLAY_ORDER.filter((s) => s !== UNGROUPED_STAGE && keys.includes(s));
  const extra = keys
    .filter((k) => k !== UNGROUPED_STAGE && !STAGE_DISPLAY_ORDER.includes(k))
    .sort((a, b) => a.localeCompare(b));
  return [...known, ...extra, ...(keys.includes(UNGROUPED_STAGE) ? [UNGROUPED_STAGE] : [])];
};

// Group classes by bucket -> class -> sections. A class with no stage set yet
// (not backfilled) falls into "Ungrouped" rather than being silently dropped.
const buildClassGroups = (classesRes: any): Record<string, ClassNode[]> => {
  const byStage: Record<string, ClassNode[]> = {};
  ((classesRes as any)?.classes || []).forEach((c: any) => {
    const stage = classBucket(c);
    (byStage[stage] ||= []).push({
      _id: c._id,
      name: c.name,
      sections: (c.sections || []).map((s: any) => ({ _id: s._id, name: s.name })),
    });
  });
  return byStage;
};

const SchoolOpeningList: React.FC = () => {
  const routes = all_routes;
  const currentSession = useSelector(selectCurrentSession);
  const currentBranch = useSelector(selectCurrentBranch);

  const [today, setToday] = useState<TodayResponse | null>(null);
  const [loadingToday, setLoadingToday] = useState(true);
  const [saving, setSaving] = useState(false);

  // Selection sources
  const [staffByRole, setStaffByRole] = useState<Record<string, OpenedForRef[]>>({});
  const [classGroups, setClassGroups] = useState<Record<string, ClassNode[]>>({});

  // Selections. selSections is keyed by "<classId>::<sectionId>" — a Section
  // record is shared/reused across many classes (e.g. every class has its own
  // "Section A" pointing at the SAME Section document), so keying by the bare
  // section id would make checking it under one class check it everywhere
  // else that section id appears too. The class+section pair keeps each
  // class's checkbox independent; only the "select all" checkboxes bulk-toggle.
  const [selStaff, setSelStaff] = useState<Set<string>>(new Set());
  const [selSections, setSelSections] = useState<Set<string>>(new Set());
  // Raw section ids opened today, as returned by the server — seeded into
  // selSections once classGroups has loaded (see seed effect below).
  const [openedSectionIdsRaw, setOpenedSectionIdsRaw] = useState<Set<string> | null>(null);
  const seededSectionsRef = useRef(false);
  const [note, setNote] = useState("");

  // History
  const [history, setHistory] = useState<OperatingDayFull[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [total, setTotal] = useState(0);
  const [range, setRange] = useState({
    startDate: dayjs().subtract(29, "day").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
  });
  const [detailRow, setDetailRow] = useState<OperatingDayFull | null>(null);
  // Which classes each stored section belongs to, for the details modal — a
  // stored section id alone can't say that (the same Section document is
  // shared across many classes; only the id survives to the database, the
  // class it was checked under doesn't). Fetched per-record using THAT
  // record's own session, not whatever session happens to be selected right
  // now — an old record's sections won't exist under a newer session.
  const [detailClassGroups, setDetailClassGroups] = useState<Record<string, ClassNode[]> | null>(null);
  const [loadingDetailClasses, setLoadingDetailClasses] = useState(false);

  // ── loaders ──────────────────────────────────────────────────────────
  const loadToday = useCallback(async () => {
    setLoadingToday(true);
    try {
      const res = await getToday();
      setToday(res);
      // Pre-check the current selection if the school is already open today.
      const of = res.record?.openedFor;
      setSelStaff(new Set((of?.staff || []).map((s) => s._id)));
      if (of?.classSections?.length) {
        // Accurate — exactly which class each section was opened under,
        // straight from storage. No inference needed.
        setSelSections(new Set(of.classSections.map((cs) => `${cs.class._id}::${cs.section._id}`)));
        setOpenedSectionIdsRaw(null);
        seededSectionsRef.current = true;
      } else {
        // Record predates classSections (or nothing opened) — fall back to
        // inferring from the deduped section ids once classGroups has
        // loaded (see seed effect). Imprecise when a section is shared
        // across classes, but that's inherent to the old data, not fixable
        // after the fact.
        setOpenedSectionIdsRaw(new Set((of?.sections || []).map((s) => s._id)));
        seededSectionsRef.current = false;
      }
      setNote(res.record?.note || "");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || "Failed to load today's status");
    } finally {
      setLoadingToday(false);
    }
  }, []);

  const loadSources = useCallback(async () => {
    // Independent try/catches — staff and classes come from unrelated
    // endpoints (staff-options is gated by school-opening permission, not
    // the general user.view a receptionist role may not have), so one
    // failing shouldn't blank out the other like a shared Promise.all did.
    try {
      const usersRes = await getStaffOptions();
      const byRole: Record<string, OpenedForRef[]> = {};
      (usersRes.data || []).forEach((u: any) => {
        const rn = roleName(u);
        if (STAFF_EXCLUDE.includes(rn.toLowerCase())) return;
        (byRole[rn] ||= []).push({ _id: u._id, firstName: u.firstName, lastName: u.lastName });
      });
      setStaffByRole(byRole);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load staff");
    }

    try {
      const classesRes = currentSession?._id
        ? await getAllClasses({ session: currentSession._id, limit: 200 } as any)
        : ({ classes: [] } as any);
      setClassGroups(buildClassGroups(classesRes));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load classes");
    }
  }, [currentSession?._id]);

  const loadHistory = useCallback(async () => {
    if (!currentBranch?._id) return;
    setLoadingHistory(true);
    try {
      const res = await list({
        startDate: range.startDate,
        endDate: range.endDate,
        page: pagination.page,
        limit: pagination.limit,
        branchId: currentBranch._id,
      });
      setHistory(res.data as OperatingDayFull[]);
      setTotal(res.pagination?.total ?? 0);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load history");
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [range.startDate, range.endDate, pagination.page, pagination.limit, currentBranch?._id]);

  useEffect(() => {
    loadToday();
    loadSources();
  }, [loadToday, loadSources]);
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // One-time seed: once both today's opened section ids AND classGroups have
  // loaded, expand the raw section ids into class+section composite keys —
  // every class that includes an opened section id starts checked for it.
  useEffect(() => {
    if (seededSectionsRef.current) return;
    if (!openedSectionIdsRaw || Object.keys(classGroups).length === 0) return;
    if (openedSectionIdsRaw.size === 0) {
      seededSectionsRef.current = true;
      return;
    }
    const seeded = new Set<string>();
    Object.values(classGroups).flat().forEach((c) => {
      c.sections.forEach((s) => {
        if (openedSectionIdsRaw.has(s._id)) seeded.add(`${c._id}::${s._id}`);
      });
    });
    setSelSections(seeded);
    seededSectionsRef.current = true;
  }, [openedSectionIdsRaw, classGroups]);

  // Details modal: only needed as a fallback for records saved before
  // classSections existed — those have the accurate pairing already and
  // need no lookup at all. Load the classes for THIS record's own session
  // so stored section ids can still be traced back to a class for old rows.
  useEffect(() => {
    if (!detailRow?.session || detailRow?.openedFor?.classSections?.length) {
      setDetailClassGroups(null);
      return;
    }
    let cancelled = false;
    setLoadingDetailClasses(true);
    getAllClasses({ session: detailRow.session, limit: 200 } as any)
      .then((res) => {
        if (!cancelled) setDetailClassGroups(buildClassGroups(res));
      })
      .catch(() => {
        if (!cancelled) setDetailClassGroups(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingDetailClasses(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detailRow?.session]);

  // (class, section-name) pairs to render, grouped by stage — mirrors
  // Section 2's own selection list (which shows stage subheadings), instead
  // of the flat, deduped, class-less section list the record itself stores.
  const detailClassSectionRows = useMemo(() => {
    type Row = { key: string; className: string; sectionName: string };
    const classSections = detailRow?.openedFor?.classSections;
    if (classSections?.length) {
      // Accurate — straight from storage, no inference or class lookup
      // needed. Grouped by the class's own stage (now populated on
      // classSections.class), same STAGE_DISPLAY_ORDER as Section 2. Within
      // a stage, left in submission order rather than re-sorted by name —
      // string-sorting Roman-numeral class names ("Grade IX" before "Grade
      // V") produces a worse order than whatever was originally selected.
      const byStage: Record<string, Row[]> = {};
      classSections.forEach((cs) => {
        const stage = classBucket(cs.class);
        (byStage[stage] ||= []).push({
          key: `${cs.class._id}::${cs.section._id}`,
          className: cs.class.name || cs.class._id,
          sectionName: cs.section.name || cs.section._id,
        });
      });
      return byStage;
    }
    // Fallback for records saved before classSections existed — infer from
    // which classes (under this record's own session) reference the stored
    // section ids. Imprecise when a section is shared across classes, but
    // that's inherent to the old data.
    if (!detailRow?.openedFor?.sections?.length || !detailClassGroups) return null;
    const openedIds = new Set(detailRow.openedFor.sections.map((s) => s._id));
    const byStage: Record<string, Row[]> = {};
    Object.entries(detailClassGroups).forEach(([stage, classes]) => {
      classes.forEach((c) => {
        c.sections.forEach((s) => {
          if (openedIds.has(s._id)) {
            (byStage[stage] ||= []).push({
              key: `${c._id}::${s._id}`,
              className: c.name,
              sectionName: s.name || s._id,
            });
          }
        });
      });
    });
    return byStage;
  }, [detailRow, detailClassGroups]);

  // Role for each opened staff member, same grouping as Section 1's own
  // selection list — reuses staffByRole (already loaded for every user, not
  // session-scoped) rather than a fresh fetch, since a user's role isn't
  // tied to which session was active when the school was opened.
  const detailStaffByRole = useMemo(() => {
    if (!detailRow?.openedFor?.staff?.length) return null;
    const roleOf = new Map<string, string>();
    Object.entries(staffByRole).forEach(([role, users]) => {
      users.forEach((u) => roleOf.set(u._id, role));
    });
    const grouped: Record<string, OpenedForRef[]> = {};
    detailRow.openedFor.staff.forEach((s) => {
      const role = roleOf.get(s._id) || "Other";
      (grouped[role] ||= []).push(s);
    });
    return grouped;
  }, [detailRow, staffByRole]);

  // ── selection helpers ────────────────────────────────────────────────
  const toggleSet = (set: Set<string>, id: string): Set<string> => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  };
  const setMany = (set: Set<string>, ids: string[], on: boolean): Set<string> => {
    const next = new Set(set);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    return next;
  };
  const allIn = (set: Set<string>, ids: string[]) => ids.length > 0 && ids.every((id) => set.has(id));

  const allStaffIds = useMemo(
    () => Object.values(staffByRole).flat().map((s) => s._id),
    [staffByRole]
  );
  // Composite "<classId>::<sectionId>" keys — matches how selSections is keyed.
  const allSectionIds = useMemo(
    () =>
      Object.values(classGroups)
        .flat()
        .flatMap((c) => c.sections.map((s) => `${c._id}::${s._id}`)),
    [classGroups]
  );
  const sectionIdsForType = (t: string) =>
    (classGroups[t] || []).flatMap((c) => c.sections.map((s) => `${c._id}::${s._id}`));
  // Underlying unique Section ids a set of composite keys resolves to — kept
  // only as the legacy fallback field the API still accepts.
  const uniqueSectionIds = (keys: Iterable<string>) =>
    Array.from(new Set(Array.from(keys).map((k) => k.split("::")[1])));
  // The real payload — exactly which class each section was checked under,
  // not just the deduped section ids (a section is often shared across many
  // classes, so the deduped list alone can't reconstruct this afterward).
  const classSectionPairs = (keys: Iterable<string>) =>
    Array.from(keys).map((k) => {
      const [classId, sectionId] = k.split("::");
      return { class: classId, section: sectionId };
    });

  const totalSelected = selStaff.size + selSections.size;
  const readOnlySources = allStaffIds.length === 0 && allSectionIds.length === 0;

  // ── actions ──────────────────────────────────────────────────────────
  const doOpen = async () => {
    if (totalSelected === 0) {
      toast.warning("Select at least one staff member, class or section to open the school for.");
      return;
    }
    setSaving(true);
    try {
      await openSchool({
        staff: Array.from(selStaff),
        sections: uniqueSectionIds(selSections),
        classSections: classSectionPairs(selSections),
        sessionId: currentSession?._id,
        note: note.trim() || undefined,
      });
      toast.success("School opened for the selected staff / classes.");
      seededSectionsRef.current = false;
      await Promise.all([loadToday(), loadHistory()]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || "Failed to open");
    } finally {
      setSaving(false);
    }
  };

  const doClose = async () => {
    setSaving(true);
    try {
      await closeSchool({ sessionId: currentSession?._id, note: note.trim() || undefined });
      toast.success("School marked closed for today.");
      setSelStaff(new Set());
      setSelSections(new Set());
      await Promise.all([loadToday(), loadHistory()]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || "Failed to close");
    } finally {
      setSaving(false);
    }
  };

  const handleRange = (r: { startDate: string; endDate: string }) => {
    setRange({
      startDate: dayjs(r.startDate).format("YYYY-MM-DD"),
      endDate: dayjs(r.endDate).format("YYYY-MM-DD"),
    });
    setPagination((p) => ({ ...p, page: 1 }));
  };

  // ── history table ────────────────────────────────────────────────────
  const columns = [
    { title: "Date", dataIndex: "date", render: (v: string) => fmtDate(v) },
    { title: "Day", dataIndex: "date", render: (v: string) => fmtDay(v) },
    { title: "Status", dataIndex: "status", render: (v: string) => <StatusBadge status={v} /> },
    {
      title: "Opened For",
      dataIndex: "openedFor",
      render: (_: unknown, r: OperatingDayFull) => {
        const s = r.openedFor?.staff?.length || 0;
        const c = r.openedFor?.sections?.length || 0;
        if (r.status !== "OPENED") return "—";
        return `${s} staff · ${c} section${c === 1 ? "" : "s"}`;
      },
    },
    {
      title: "By",
      dataIndex: "openedBy",
      render: (_: unknown, r: OperatingDayFull) =>
        r.status === "OPENED"
          ? fullName(r.openedBy)
          : r.status === "CLOSED" && r.closedBy
          ? fullName(r.closedBy)
          : "—",
    },
    {
      title: "Time",
      dataIndex: "openedAt",
      render: (_: unknown, r: OperatingDayFull) =>
        fmtTime(r.status === "OPENED" ? r.openedAt : r.closedAt),
    },
    {
      title: "Details",
      dataIndex: "action",
      render: (_: unknown, r: OperatingDayFull) =>
        r._id ? (
          <button
            type="button"
            className="btn btn-sm btn-outline-light"
            data-bs-toggle="modal"
            data-bs-target="#opening_detail_modal"
            onClick={() => setDetailRow(r)}
          >
            <i className="ti ti-eye me-1" /> View
          </button>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
  ];

  const paginationConfig = {
    currentPage: pagination.page,
    limit: pagination.limit,
    total,
    onChange: (page: number, pageSize: number) => setPagination({ page, limit: pageSize }),
  };

  const status = today?.status || "PENDING";

  // ── render ───────────────────────────────────────────────────────────
  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">School Opening</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">Management</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    School Opening
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          {/* Today panel */}
          <div className="card">
            <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <div className="text-muted small">Today</div>
                <h5 className="mb-1">
                  {fmtDate(today?.date)} · {fmtDay(today?.date)}
                </h5>
                <StatusBadge status={status} />
                {totalSelected > 0 && (
                  <span className="ms-2 text-muted small">
                    {selStaff.size} staff · {selSections.size} sections selected
                  </span>
                )}
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={saving || loadingToday || totalSelected === 0}
                  onClick={doOpen}
                >
                  <i className="ti ti-lock-open me-1" />
                  {status === "OPENED" ? "Update Opening" : "Open School"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={saving || loadingToday}
                  onClick={doClose}
                >
                  <i className="ti ti-lock me-1" /> Close
                </button>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Section 1 — Staff */}
            <div className="col-lg-5">
              <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">Section 1 — Staff</h5>
                  <div className="form-check mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="all_staff"
                      checked={allIn(selStaff, allStaffIds)}
                      onChange={(e) => setSelStaff((s) => setMany(s, allStaffIds, e.target.checked))}
                    />
                    <label className="form-check-label small" htmlFor="all_staff">
                      Select all staff
                    </label>
                  </div>
                </div>
                <div className="card-body" style={{ maxHeight: 480, overflowY: "auto" }}>
                  {Object.keys(staffByRole).length === 0 && (
                    <p className="text-muted mb-0">No staff found for this campus.</p>
                  )}
                  {Object.entries(staffByRole).map(([role, users]) => {
                    const ids = users.map((u) => u._id);
                    return (
                      <div key={role} className="mb-3">
                        <div className="form-check border-bottom pb-1 mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`role_${role}`}
                            checked={allIn(selStaff, ids)}
                            onChange={(e) => setSelStaff((s) => setMany(s, ids, e.target.checked))}
                          />
                          <label className="form-check-label fw-semibold text-capitalize" htmlFor={`role_${role}`}>
                            {role} <span className="text-muted">({users.length})</span>
                          </label>
                        </div>
                        <div className="ps-3">
                          {users.map((u) => (
                            <div key={u._id} className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`staff_${u._id}`}
                                checked={selStaff.has(u._id)}
                                onChange={() => setSelStaff((s) => toggleSet(s, u._id))}
                              />
                              <label className="form-check-label" htmlFor={`staff_${u._id}`}>
                                {fullName(u)}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Section 2 — Students */}
            <div className="col-lg-7">
              <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">Section 2 — Students</h5>
                  <div className="form-check mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="all_sections"
                      checked={allIn(selSections, allSectionIds)}
                      onChange={(e) => setSelSections((s) => setMany(s, allSectionIds, e.target.checked))}
                    />
                    <label className="form-check-label small" htmlFor="all_sections">
                      Select all classes
                    </label>
                  </div>
                </div>
                <div className="card-body" style={{ maxHeight: 480, overflowY: "auto" }}>
                  {allSectionIds.length === 0 && (
                    <p className="text-muted mb-0">No classes / sections found for this session.</p>
                  )}
                  {orderedBuckets(Object.keys(classGroups)).filter((t) => classGroups[t]?.length).map((t) => {
                    const typeIds = sectionIdsForType(t);
                    return (
                      <div key={t} className="mb-3">
                        <div className="form-check border-bottom pb-1 mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`type_${t}`}
                            checked={allIn(selSections, typeIds)}
                            onChange={(e) => setSelSections((s) => setMany(s, typeIds, e.target.checked))}
                          />
                          <label className="form-check-label fw-semibold" htmlFor={`type_${t}`}>
                            {t}
                          </label>
                        </div>
                        <div className="ps-3">
                          {(classGroups[t] || []).map((c) => {
                            // Composite keys — a Section record (e.g. "Section A") is
                            // shared across many classes, so this class's checkbox
                            // must only track ITS OWN pairing, not the bare section id.
                            const cIds = c.sections.map((s) => `${c._id}::${s._id}`);
                            return (
                              <div key={c._id} className="mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`class_${c._id}`}
                                    checked={allIn(selSections, cIds)}
                                    disabled={cIds.length === 0}
                                    onChange={(e) => setSelSections((s) => setMany(s, cIds, e.target.checked))}
                                  />
                                  <label className="form-check-label fw-medium" htmlFor={`class_${c._id}`}>
                                    {c.name}
                                    {cIds.length === 0 && (
                                      <span className="text-muted small"> (no sections)</span>
                                    )}
                                  </label>
                                </div>
                                <div className="ps-4 d-flex flex-wrap gap-3">
                                  {c.sections.map((s) => {
                                    const key = `${c._id}::${s._id}`;
                                    return (
                                      <div key={s._id} className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          id={`sec_${key}`}
                                          checked={selSections.has(key)}
                                          onChange={() => setSelSections((prev) => toggleSet(prev, key))}
                                        />
                                        <label className="form-check-label" htmlFor={`sec_${key}`}>
                                          Section {s.name}
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Optional note */}
          <div className="card">
            <div className="card-body">
              <label className="form-label mb-1">Note (optional)</label>
              <textarea
                className="form-control"
                rows={2}
                placeholder="e.g. reason for closure / partial opening"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>

          {/* History */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h5 className="mb-3">Opening History</h5>
              <div className="me-2 mb-2">
                <PredefinedDateRanges onDateSelect={handleRange} />
              </div>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                Selection={false}
                setSearchText={() => {}}
                dataSource={history}
                columns={columns}
                loading={loadingHistory}
                pagination={paginationConfig}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Details modal */}
      <div className="modal fade" id="opening_detail_modal" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Opening details — {fmtDate(detailRow?.date)} ({fmtDay(detailRow?.date)})
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              {detailRow ? (
                <>
                  <div className="d-flex flex-wrap gap-4 mb-3">
                    <div>
                      <div className="text-muted small">Status</div>
                      <StatusBadge status={detailRow.status} />
                    </div>
                    <div>
                      <div className="text-muted small">Opened by</div>
                      <div>{detailRow.status === "OPENED" ? fullName(detailRow.openedBy) : "—"}</div>
                    </div>
                    <div>
                      <div className="text-muted small">Opened at</div>
                      <div>{fmtTime(detailRow.openedAt)}</div>
                    </div>
                    {detailRow.status === "CLOSED" && (
                      <>
                        <div>
                          <div className="text-muted small">Closed by</div>
                          <div>{detailRow.autoClosed ? "System (auto)" : fullName(detailRow.closedBy)}</div>
                        </div>
                        <div>
                          <div className="text-muted small">Closed at</div>
                          <div>{fmtTime(detailRow.closedAt)}</div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="mb-2">
                        Staff <span className="text-muted">({detailRow.openedFor?.staff?.length || 0})</span>
                      </h6>
                      {!detailRow.openedFor?.staff?.length ? (
                        <p className="text-muted mb-0">—</p>
                      ) : detailStaffByRole ? (
                        Object.entries(detailStaffByRole).map(([role, users]) => (
                          <div key={role} className="mb-2">
                            <div className="fw-semibold small">
                              {role} <span className="text-muted">({users.length})</span>
                            </div>
                            <ul className="mb-0">
                              {users.map((s) => (
                                <li key={s._id}>{fullName(s)}</li>
                              ))}
                            </ul>
                          </div>
                        ))
                      ) : (
                        <ul className="mb-0">
                          {detailRow.openedFor.staff.map((s) => (
                            <li key={s._id}>{fullName(s)}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="col-md-6">
                      <h6 className="mb-2">
                        Classes / Sections{" "}
                        <span className="text-muted">
                          ({detailClassSectionRows
                            ? Object.values(detailClassSectionRows).reduce((n, rows) => n + rows.length, 0)
                            : detailRow.openedFor?.sections?.length ?? 0}
                          )
                        </span>
                      </h6>
                      {!detailRow.openedFor?.sections?.length ? (
                        <p className="text-muted mb-0">—</p>
                      ) : loadingDetailClasses ? (
                        <p className="text-muted mb-0">Loading…</p>
                      ) : detailClassSectionRows && Object.keys(detailClassSectionRows).length ? (
                        orderedBuckets(Object.keys(detailClassSectionRows)).filter((t) => detailClassSectionRows[t]?.length).map((t) => (
                          <div key={t} className="mb-2">
                            <div className="fw-semibold small">{t}</div>
                            <ul className="mb-0">
                              {detailClassSectionRows[t].map((r) => (
                                <li key={r.key}>
                                  {r.className} — Section {r.sectionName}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))
                      ) : (
                        // Couldn't resolve the record's session, or none of its
                        // classes reference these section ids anymore — fall
                        // back to the bare (deduped) section names.
                        <ul className="mb-0">
                          {detailRow.openedFor.sections.map((s) => (
                            <li key={s._id}>{s.name || s._id}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  {detailRow.note && (
                    <div className="mt-3">
                      <div className="text-muted small">Note</div>
                      <div>{detailRow.note}</div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted mb-0">No record.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SchoolOpeningList;
